import { useGetAuctionForms } from "apollo/hooks/queries";
import clsx from "clsx";
import {
  CLINICAL_CONSULTATION_ACTION,
  DOCS_FORM_NAMES,
  RECARE_FILE_TYPE,
  SEARCH_ACTION_GENERATE_FORM,
} from "core/consts";
import { getSearchActions } from "core/model/auctions";
import { useIsDocsAndExtractActive } from "core/model/careseekers";
import { getClinicalConsultationActions } from "core/model/clinicalConsultations";
import { formatFromNow } from "core/model/utils/dates";
import { activateSummitDemoAdditionalDocTiles } from "core/model/utils/featureFlags";
import {
  Auction,
  AuctionForm,
  ClinicalConsultation,
  ClinicalConsultationAction,
  Patient,
  RecareFileType,
  SearchAction,
} from "core/types";
import { Button, Chip, ChipProps } from "ds/ui";
import { useRecareDocsContext } from "dsl/ecosystems/RecareDocs/context";
import { getAuctionForm } from "dsl/ecosystems/RecareDocs/Modal/Forms/DischargeLetters/useGetForm";
import { useCareseekerHandlerUrls } from "dsl/hooks/useNavigationHandlers";
import {
  CheckIcon,
  ClipboardListIcon,
  EditIcon,
  ExternalLinkIcon,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useLoggedCareseeker } from "reduxentities/hooks";
import { useLocale, useTranslations } from "translations";

type DocsTileProps = {
  chip: Pick<ChipProps, "color" | "id" | "children">;
  id: string;
  isDisabled?: boolean;
  shouldShow: boolean;
  title: string;
  form?: AuctionForm;
} & (
    | {
      href: string;
      onPress?: never;
    }
    | {
      href?: never;
      onPress: () => void;
    }
  );

const DocTile = ({
  chip,
  form,
  href,
  id,
  isDisabled,
  onPress,
  shouldShow,
  title,
}: DocsTileProps) => {
  const navigate = useNavigate();
  const translations = useTranslations();
  const locale = useLocale();
  const formAssigneeDisplayName = getFormAssigneeDisplayName(form);
  const updatedAt = form?.updated_at;
  const isDisabledTile = isDisabled === true;

  if (!shouldShow) return null;

  return (
    <div
      className="flex h-[200px] w-full max-w-[300px] flex-col gap-3 rounded-[12px] bg-white p-6 shadow-sm"
      data-testid={`doc-tile-${id}`}
    >
      <div
        className={clsx("flex w-full justify-items-center", {
          "justify-between": updatedAt,
        })}
      >
        <Chip {...chip} id={`doc-tile-chip-${id}`} size="sm" variant="flat" />
        {updatedAt && (
          <Chip
            color="accent"
            id={`doc-tile-chip-${id}-worked-on`}
            size="sm"
            startContent={<CheckIcon aria-hidden className="h-4 w-4" />}
            variant="flat"
          >
            {
              translations.fileSection.creatableDocuments.tileGeneric
                .workedOnChip
            }
          </Chip>
        )}
      </div>
      <div
        className="flex h-full flex-col justify-between"
        data-testid={`doc-tile-edit-container-${id}`}
      >
        <h2 className="text-heading-lg font-bold">{title}</h2>
        {formAssigneeDisplayName && (
          <div className="flex flex-col">
            <p className="text-body-md text-gray-600">
              {
                translations.fileSection.creatableDocuments.tileGeneric
                  .lastEdited
              }
            </p>
            <p className="text-body-md text-gray-600">{`${formAssigneeDisplayName}, ${formatFromNow(
              updatedAt ?? 0,
              locale,
            )}`}</p>
          </div>
        )}
        <Button
          as={href && !isDisabledTile ? "a" : undefined}
          color="primary"
          endContent={updatedAt ? <EditIcon /> : <ExternalLinkIcon />}
          href={href && !isDisabledTile ? href : undefined}
          id={`doc-tile-button-${id}-${updatedAt ? "edit" : "open"}`}
          isDisabled={isDisabledTile}
          trackEventProps={{
            id: `doc-tile-button-${updatedAt ? "edit" : "open"}`,
          }}
          variant="ghost"
          onClick={
            href && !isDisabledTile ? (e) => e.preventDefault() : undefined
          }
          onPress={() => {
            if (isDisabledTile) {
              return;
            }
            if (href) {
              navigate(href);
            } else {
              onPress?.();
            }
          }}
        >
          {updatedAt
            ? translations.fileSection.creatableDocuments.tileGeneric
              .openAlreadyWorkedOnForm
            : translations.fileSection.creatableDocuments.tileGeneric
              .buttonOpenForm}
        </Button>
      </div>
    </div>
  );
};

const isFormActive = (
  action: SearchAction | ClinicalConsultationAction,
  ...formNames: RecareFileType[]
) => {
  return (
    !action.context?.disabled &&
    formNames.some((formName) => action.context?.values?.includes(formName))
  );
};

function getSummitDemoDocTiles(
  summitFlag: boolean,
  translations: ReturnType<typeof useTranslations>,
): DocsTileProps[] {
  if (!summitFlag) {
    return [];
  }

  return [
    {
      chip: {
        children: translations.extractDocs.dischargeLetter.tileChip,
        color: "success",
        id: "summit-chip-anesthesia-protocoll",
      },
      id: "summit-anesthesia-protocoll",
      isDisabled: true,
      onPress: () => console.log("summit-anesthesia-protocoll"),
      shouldShow: true,
      title: translations.fileSection.creatableDocuments.titles.anesthesiaProtocoll,
    },
    {
      chip: {
        children: translations.fileSection.creatableDocuments.tileCategory.rehab,
        color: "accent",
        id: "summit-chip-pvk-ahb-application",
      },
      id: "summit-pvk-ahb-application",
      isDisabled: true,
      onPress: () => console.log("summit-pvk-ahb-application"),
      shouldShow: true,
      title: translations.fileSection.creatableDocuments.titles.pvkAhbApplication,
    },
    {
      chip: {
        children: translations.extractDocs.dischargeLetter.tileChip,
        color: "success",
        id: "summit-chip-discharge-summary-inpatient",
      },
      id: "summit-discharge-summary-inpatient-treatment",
      isDisabled: true,
      onPress: () => console.log("summit-discharge-summary-inpatient-treatment"),
      shouldShow: true,
      title: translations.fileSection.creatableDocuments.titles.dischargeSummaryInpatientTreatment,
    },
    {
      chip: {
        children: translations.fileSection.creatableDocuments.tileCategory.care,
        color: "primary",
        id: "summit-chip-care-transition-report",
      },
      id: "summit-care-transition-report",
      isDisabled: true,
      onPress: () => console.log("summit-care-transition-report"),
      shouldShow: true,
      title: translations.fileSection.creatableDocuments.titles.careTransitionReport,
    },
    {
      chip: {
        children: translations.fileSection.creatableDocuments.tileCategory.care,
        color: "primary",
        id: "summit-chip-care-level-application",
      },
      id: "summit-care-level-application",
      isDisabled: true,
      onPress: () => console.log("summit-care-level-application"),
      shouldShow: true,
      title: translations.fileSection.creatableDocuments.titles.careLevelApplication,
    },
    {
      chip: {
        children: translations.fileSection.creatableDocuments.tileCategory.care,
        color: "primary",
        id: "summit-chip-intensive-care-request",
      },
      id: "summit-intensive-care-request",
      isDisabled: true,
      onPress: () => console.log("summit-intensive-care-request"),
      shouldShow: true,
      title: translations.fileSection.creatableDocuments.titles.intensiveCareRequest,
    },
    {
      chip: {
        children: translations.fileSection.creatableDocuments.tileCategory.care,
        color: "primary",
        id: "summit-chip-care-at-home-request",
      },
      id: "summit-care-at-home-request",
      isDisabled: true,
      onPress: () => console.log("summit-care-at-home-request"),
      shouldShow: true,
      title: translations.fileSection.creatableDocuments.titles.careAtHomeRequest,
    },
    {
      chip: {
        children: translations.extractDocs.dischargeLetter.tileChip,
        color: "success",
        id: "summit-chip-day-clinic-report",
      },
      id: "summit-day-clinic-report",
      isDisabled: true,
      onPress: () => console.log("summit-day-clinic-report"),
      shouldShow: true,
      title: translations.fileSection.creatableDocuments.titles.dayClinicReport,
    },
  ];
}

export const getFormAssigneeDisplayName = (
  form: AuctionForm | undefined,
): string | undefined => {
  const account = form?.last_event?.account;

  if (!account?.first_name || !account?.last_name) {
    return undefined;
  }

  const initial = account.first_name[0].toUpperCase();

  return `${initial}. ${account.last_name}`;
};

const useGetDocTiles = ({
  auction,
  consultation,
  patient,
}: {
  auction: Auction | undefined;
  consultation: ClinicalConsultation | undefined;
  patient: Patient;
}): DocsTileProps[] | null => {
  const translations = useTranslations();
  const loggedCareseeker = useLoggedCareseeker();
  const summitDemoDocTilesFlag = activateSummitDemoAdditionalDocTiles(
    loggedCareseeker?.id,
  );
  const { isDischargeLetterActive, isRedesignActive } =
    useIsDocsAndExtractActive();

  const careseekerUrls = useCareseekerHandlerUrls();
  const { setFormModal } = useRecareDocsContext();

  const [, auctionForms] = useGetAuctionForms({
    auctionId: auction?.id ?? 0,
    skip: !auction?.id,
  });
  const { [SEARCH_ACTION_GENERATE_FORM]: generateFormSearchAction } =
    getSearchActions(auction ?? null);

  const {
    [CLINICAL_CONSULTATION_ACTION.GENERATE_FORM]:
    generateFormClinicalConsultationAction,
  } = getClinicalConsultationActions(consultation ?? null);

  if (!isRedesignActive && !isDischargeLetterActive && !consultation) {
    return null;
  }

  if (
    !generateFormSearchAction?.context?.values?.length &&
    !generateFormClinicalConsultationAction
  ) {
    return null;
  }

  const forms = consultation?.forms ?? auctionForms ?? [];

  const { form: entForm } = getAuctionForm({
    auctionForms: forms,
    formName: DOCS_FORM_NAMES.DISCHARGE_LETTER_ENT,
  });
  const { form: cardiologyForm } = getAuctionForm({
    auctionForms: forms,
    formName: DOCS_FORM_NAMES.DISCHARGE_LETTER_CARDIOLOGY,
  });
  const { form: transitionalCareForm } = getAuctionForm({
    auctionForms: forms,
    formName: DOCS_FORM_NAMES.TRANSITIONAL_CARE,
  });

  if (generateFormClinicalConsultationAction) {
    return [
      {
        chip: {
          children: translations.extractDocs.dischargeLetter.tileChip,
          color: "success",
          id: "discharge-ent-chip",
        },
        form: entForm,
        id: "discharge-ent",
        onPress: () => setFormModal(DOCS_FORM_NAMES.DISCHARGE_LETTER_ENT),
        shouldShow: isFormActive(
          generateFormClinicalConsultationAction,
          RECARE_FILE_TYPE.DISCHARGE_LETTER_ENT,
        ),
        title: translations.extractDocs.dischargeLetter.entTileTitle,
      },
      {
        chip: {
          children: translations.extractDocs.dischargeLetter.tileChip,
          color: "success",
          id: "discharge-cardiology-chip",
        },
        form: cardiologyForm,
        id: "discharge-cardiology",
        onPress: () =>
          setFormModal(DOCS_FORM_NAMES.DISCHARGE_LETTER_CARDIOLOGY),
        shouldShow: isFormActive(
          generateFormClinicalConsultationAction,
          RECARE_FILE_TYPE.DISCHARGE_LETTER_CARDIOLOGY,
        ),
        title: translations.extractDocs.dischargeLetter.cardiologyTileTitle,
      },
    ];
  }

  if (!auction) {
    console.error("auction is not defined in docs section");
    return null;
  }

  return [
    {
      chip: {
        children:
          translations.fileSection.creatableDocuments.tileCategory.rehab,
        color: "accent",
        id: "rehab",
      },
      href: careseekerUrls.goToRehabForms({
        auctionId: auction.id,
        isDocsRedesignActive: true,
        patientId: patient.id,
      }),
      id: "rehab",
      shouldShow:
        isRedesignActive &&
        isFormActive(
          generateFormSearchAction,
          RECARE_FILE_TYPE.DRV_MEDICAL_FORM,
          RECARE_FILE_TYPE.DRV_GENERAL_FORM,
          RECARE_FILE_TYPE.GKV_MEDICAL_FORM,
          RECARE_FILE_TYPE.GKV_GENERAL_FORM,
        ),
      title: translations.fileSection.creatableDocuments.tileRehab.title,
    },
    {
      chip: {
        children: translations.fileSection.creatableDocuments.tileCategory.care,
        color: "primary",
        id: "t-care",
      },
      form: transitionalCareForm,
      id: "t-care",
      onPress: () => setFormModal(DOCS_FORM_NAMES.TRANSITIONAL_CARE),
      shouldShow:
        isRedesignActive &&
        isFormActive(
          generateFormSearchAction,
          RECARE_FILE_TYPE.TRANSITIONAL_CARE,
        ),
      title:
        translations.fileSection.creatableDocuments.tileTransitionalCare.title,
    },
    {
      chip: {
        children: translations.extractDocs.dischargeLetter.tileChip,
        color: "success",
        id: "discharge-ent-chip",
      },
      form: entForm,
      id: "discharge-ent",
      onPress: () => setFormModal(DOCS_FORM_NAMES.DISCHARGE_LETTER_ENT),
      shouldShow: isFormActive(
        generateFormSearchAction,
        RECARE_FILE_TYPE.DISCHARGE_LETTER_ENT,
      ),
      title: translations.extractDocs.dischargeLetter.entTileTitle,
    },
    {
      chip: {
        children: translations.extractDocs.dischargeLetter.tileChip,
        color: "success",
        id: "discharge-cardiology-chip",
      },
      form: cardiologyForm,
      id: "discharge-cardiology",
      onPress: () => setFormModal(DOCS_FORM_NAMES.DISCHARGE_LETTER_CARDIOLOGY),
      shouldShow: isFormActive(
        generateFormSearchAction,
        RECARE_FILE_TYPE.DISCHARGE_LETTER_CARDIOLOGY,
      ),
      title: translations.extractDocs.dischargeLetter.cardiologyTileTitle,
    },
    ...getSummitDemoDocTiles(summitDemoDocTilesFlag, translations),
  ];
};
export const DocsSection = ({
  auction,
  consultation,
  patient,
}: {
  auction: Auction | undefined;
  consultation: ClinicalConsultation | undefined;
  patient: Patient;
}) => {
  const translations = useTranslations();
  const docTiles = useGetDocTiles({
    auction,
    consultation,
    patient,
  });

  if (!docTiles) {
    return null;
  }

  return (
    <div className="flex w-full flex-col gap-2 pt-2 pb-6 desktop:pb-0">
      <div className="flex gap-x-1 py-3 pl-7">
        <ClipboardListIcon className="h-[23.75px] w-[18.75px]" />
        <div className="flex flex-col gap-0 pl-2">
          <h2 className="text-heading-xl font-bold">
            {translations.fileSection.fileListComponent.titleCreatableDocuments}
          </h2>
          <p className="text-body-md">
            {
              translations.fileSection.fileListComponent
                .descriptionCreatableDocuments
            }
          </p>
        </div>
      </div>
      <div className="grid w-full grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
        {docTiles.map((doc) => (
          <DocTile key={doc.id} {...doc} />
        ))}
      </div>
    </div>
  );
};
